import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Box,
  CircularProgress,
  Paper,
  TablePagination,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Menu,
  ListItem,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Typography,
  Icon,
  Divider,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isoWeek);
import axios from "axios";
import {
  PictureAsPdf,
  GetApp,
  PlayCircle,
  Image as ImageIcon,
  CheckCircle,
  Cancel,
  Search,
  FileDownload,
  YouTube,
  InsertDriveFile,
  Link,
  Visibility,
  Download,
  Close,
  Description,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import MDButton from "components/MDButton";
import { useLocation } from "react-router-dom";

const AdminWeeklyReportManager = () => {
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState(() => {
    if (location?.state?.month && location?.state?.year) {
      return dayjs()
        .year(location.state.year)
        .month(location.state.month - 1);
    }
    return dayjs();
  });
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [viewPdfUrl, setViewPdfUrl] = useState(null);
  const [mediaListDialog, setMediaListDialog] = useState({
    open: false,
    media: [],
    userName: "",
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    reportId: null,
    userName: "",
    weekNumber: "",
  });
  const [deleteFileConfirmation, setDeleteFileConfirmation] = useState({
    open: false,
    reportId: null,
    fileUrl: null,
    userName: "",
    weekNumber: "",
  });

  const handleOpenMediaDialog = (media, userName) => {
    setMediaListDialog({
      open: true,
      media: media || [],
      userName: userName || "User",
    });
  };

  const handleCloseMediaDialog = () => {
    setMediaListDialog({
      open: false,
      media: [],
      userName: "",
    });
  };

  const [message, setMessage] = useState({
    status: "",
    title: "",
    msg: "",
  });

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filter State - now arrays for multi-select
  const [selectedDistricts, setSelectedDistricts] = useState(["All"]);
  const [selectedUsers, setSelectedUsers] = useState(() => {
    if (location?.state?.userId) {
      const uid = location.state.userId;
      return [parseInt(uid, 10)];
    }
    return ["All"];
  });
  const [districts, setDistricts] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [statusFilter, setStatusFilter] = useState(() => {
    if (location?.state?.status) {
      return location.state.status;
    }
    return "All";
  });
  const [columnVisibility, setColumnVisibility] = useState({
    Profile: true,
    "User Name": true,
    District: true,
    "Week 1": true,
    "Week 2": true,
    "Week 3": true,
    "Week 4": true,
    "Last Submission": true,
    Action: true,
  });
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [sortField, setSortField] = useState("user_name");
  const [sortOrder, setSortOrder] = useState("asc");

  const handleColumnMenuOpen = (event) => setColumnMenuAnchor(event.currentTarget);
  const handleColumnMenuClose = () => setColumnMenuAnchor(null);

  const toggleColumnVisibility = (column) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  // Range: May of (Current Year - 1) to March of (Current Year + 1)
  const minDateLimit = dayjs().subtract(1, "year").month(4).startOf("month");
  const maxDateLimit = dayjs().add(1, "year").month(2).endOf("month");

  // Fetch districts and users list on mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        // Fetch districts
        const districtResponse = await axios.get(`${process.env.REACT_APP_API_URL}/districts/all`, {
          withCredentials: true,
        });
        setDistricts(districtResponse.data?.data || []);
      } catch (error) {
        console.error("Error fetching dropdown data", error);
      }
    };
    fetchDropdownData();
  }, []);

  // Fetch users when selectedDistricts changes
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const params = {};
        // If specific districts selected (and not All), pass them
        if (selectedDistricts.length > 0 && !selectedDistricts.includes("All")) {
          params.districts = selectedDistricts.join(",");
        }

        const response = await axios.get(`${process.env.REACT_APP_API_URL}/reports/weekly/users`, {
          params: params,
          withCredentials: true,
        });
        setUsersList(response.data?.data || []);
      } catch (error) {
        console.error("Error fetching users", error);
      }
    };
    fetchUsers();
  }, [selectedDistricts]);

  useEffect(() => {
    fetchData();
  }, [
    selectedDate,
    page,
    rowsPerPage,
    selectedDistricts,
    selectedUsers,
    statusFilter,
    sortField,
    sortOrder,
  ]);

  // Handle filter from navigation state (Dashboard/Notification click)
  useEffect(() => {
    if (location.state) {
      let stateChanged = false;

      // Month and year
      if (location.state.month && location.state.year) {
        const newDate = dayjs()
          .year(location.state.year)
          .month(location.state.month - 1);
        setSelectedDate(newDate);
        stateChanged = true;
      }

      // User ID
      if (location.state.userId) {
        const uid = location.state.userId;
        setSelectedUsers([parseInt(uid, 10)]);
        stateChanged = true;
      }

      // Status filter
      if (location.state.status) {
        setStatusFilter(location.state.status);
        stateChanged = true;
      }

      if (stateChanged) {
        // Clear state to avoid re-applying on every render or back navigation
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state]);

  const handleChange = (event, setFunction, currentSelection) => {
    const {
      target: { value },
    } = event;

    let newValues = typeof value === "string" ? value.split(",") : value;

    if (newValues.includes("All")) {
      if (currentSelection.includes("All")) {
        // "All" was already selected, so other items were selected. Remove "All".
        setFunction(newValues.filter((val) => val !== "All"));
      } else {
        // "All" was just selected. Clear others.
        setFunction(["All"]);
      }
    } else {
      // "All" is not in the new value
      if (newValues.length === 0) {
        setFunction(["All"]);
      } else {
        setFunction(newValues);
      }
    }
    setPage(0); // Reset pagination when filters change
  };

  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setSortOrder(newOrder);
    setSortField(field);
    setPage(0);
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Calculate offset based on current page and rows per page
      const offset = page * rowsPerPage;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reports/weekly/admin-summary`,
        {
          params: {
            month: selectedDate.month() + 1,
            year: selectedDate.year(),
            offset: offset,
            limit: rowsPerPage,
            // Send arrays as comma-separated strings for backend, empty if "All"
            districts:
              selectedDistricts.includes("All") || selectedDistricts.length === 0
                ? ""
                : selectedDistricts.join(","),
            users:
              selectedUsers.includes("All") || selectedUsers.length === 0
                ? ""
                : selectedUsers.join(","),
            status: statusFilter === "All" ? "" : statusFilter,
            sortField: sortField,
            sortOrder: sortOrder,
          },
          withCredentials: true,
        }
      );
      setUsers(response.data.data);
      setTotalCount(response.data.total || response.data.data.length);
    } catch (error) {
      console.error("Error fetching admin reports data", error);
      setNotification({ open: true, message: "Failed to fetch data", severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const getWeeksForMonth = (date) => {
    const startOfMonth = dayjs(date).startOf("month");
    const weeks = [];

    // Find the first Monday of the month
    let currentMonday = startOfMonth.isoWeekday(1);
    if (currentMonday.isBefore(startOfMonth, "day")) {
      currentMonday = currentMonday.add(7, "days");
    }

    // Generate exactly 4 weeks
    for (let i = 1; i <= 4; i++) {
      const weekStart = currentMonday;
      const weekEnd = currentMonday.add(6, "days");
      weeks.push({
        value: i,
        label: `Week ${i} (${weekStart.format("D")} - ${weekEnd.format("D")})`,
        start: weekStart,
        end: weekEnd,
      });
      currentMonday = currentMonday.add(7, "days");
    }
    return weeks;
  };;
  const getFileName = (url) => {
    if (!url) return "";
    return url.split("/").pop().split("-").slice(1).join("-") || url.split("/").pop();
  };

  const handleClosePdf = () => setViewPdfUrl(null);

  const handlePreview = (url) => {
    if (!url) return;
    const cleanUrl = url.split("?")[0].toLowerCase();
    const isWord = cleanUrl.endsWith(".doc") || cleanUrl.endsWith(".docx");

    if (isWord) {
      const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      setViewPdfUrl(officeUrl);
    } else {
      setViewPdfUrl(url);
    }
  };

  const handleDeleteReport = async () => {
    const { reportId } = deleteConfirmation;
    if (!reportId) return;

    setIsLoading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/reports/weekly/${reportId}`, {
        withCredentials: true,
      });
      setNotification({ open: true, message: "Report deleted successfully", severity: "success" });
      fetchData(); // Refresh table
    } catch (error) {
      console.error("Delete failed", error);
      setNotification({
        open: true,
        message: error.response?.data?.message || "Delete failed",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      setDeleteConfirmation({ open: false, reportId: null, userName: "", weekNumber: "" });
    }
  };

  const handleDeleteFile = async () => {
    const { reportId, fileUrl } = deleteFileConfirmation;
    if (!reportId || !fileUrl) return;

    setIsLoading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/reports/weekly/${reportId}/file`, {
        data: { url: fileUrl },
        withCredentials: true,
      });
      setNotification({ open: true, message: "File deleted successfully", severity: "success" });
      fetchData(); // Refresh table
    } catch (error) {
      console.error("Delete file failed", error);
      setNotification({
        open: true,
        message: error.response?.data?.message || "Delete file failed",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      setDeleteFileConfirmation({
        open: false,
        reportId: null,
        fileUrl: null,
        userName: "",
        weekNumber: "",
      });
    }
  };

  const handleDownload = async (url, title) => {
    if (!url) return;
    try {
      const cleanUrl = url.split("?")[0].toLowerCase();
      const originalExt = cleanUrl.split(".").pop();
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${title.replace(/\s+/g, "_")}_${dayjs().format("YYYYMMDD")}.${originalExt}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setMessage({ status: "success", title: "Success", msg: "File downloaded successfully!" });
    } catch (error) {
      console.error("Download failed", error);
      setMessage({ status: "error", title: "Error", msg: "Download failed. Please try again." });
    }
  };

  const DocumentActions = ({ url, title, isPdf, isWord, onDelete }) => {
    if (!url)
      return (
        <MDTypography variant="caption" color="text">
          -
        </MDTypography>
      );
    return (
      <MDBox display="flex" gap={1} justifyContent="center" alignItems="center">
        <Box display="flex" alignItems="center" gap={0.5}>
          {isPdf ? (
            <PictureAsPdf sx={{ fontSize: "1rem", color: "#f44336" }} />
          ) : isWord ? (
            <Description sx={{ fontSize: "1rem", color: "#1976d2" }} />
          ) : null}
        </Box>
        <Tooltip title="View">
          <IconButton
            size="small"
            color="info"
            onClick={() => handlePreview(url)}
            sx={{ bgcolor: "#e3f2fd", "&:hover": { bgcolor: "#bbdefb" }, width: 24, height: 24 }}
          >
            <Visibility sx={{ fontSize: "0.875rem" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton
            size="small"
            color="success"
            onClick={() => handleDownload(url, title)}
            sx={{ bgcolor: "#e8f5e9", "&:hover": { bgcolor: "#c8e6c9" }, width: 24, height: 24 }}
          >
            <Download sx={{ fontSize: "0.875rem" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete File">
          <IconButton
            size="small"
            color="error"
            onClick={() => onDelete()}
            sx={{ bgcolor: "#ffebee", "&:hover": { bgcolor: "#ffcdd2" }, width: 24, height: 24 }}
          >
            <Close sx={{ fontSize: "0.875rem" }} />
          </IconButton>
        </Tooltip>
      </MDBox>
    );
  };

  DocumentActions.propTypes = {
    url: PropTypes.string,
    title: PropTypes.string,
    isPdf: PropTypes.bool,
    isWord: PropTypes.bool,
    onDelete: PropTypes.func,
  };

  const handleExportExcel = async () => {
    try {
      setNotification({ open: true, message: "Preparing export...", severity: "info" });
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reports/weekly/admin-summary`,
        {
          params: {
            month: selectedDate.month() + 1,
            year: selectedDate.year(),
            offset: 0,
            limit: 10000,
            districts:
              selectedDistricts.includes("All") || selectedDistricts.length === 0
                ? ""
                : selectedDistricts.join(","),
            users:
              selectedUsers.includes("All") || selectedUsers.length === 0
                ? ""
                : selectedUsers.join(","),
            status: statusFilter === "All" ? "" : statusFilter,
            sortField: sortField,
            sortOrder: sortOrder,
          },
          withCredentials: true,
        }
      );

      const allUsers = response.data.data;

      if (!allUsers || allUsers.length === 0) {
        setNotification({ open: true, message: "No data to export", severity: "warning" });
        return;
      }

      const wb = XLSX.utils.book_new();

      const header = ["S.No", "User Name", "District", "Reporting Month/Year"];
      weeks.forEach((w) => {
        header.push(`Week ${w.value} - Status`);
        header.push(`Week ${w.value} - Submitted On`);
        header.push(`Week ${w.value} - Report`);
        header.push(`Week ${w.value} - Media Links`);
      });

      const sanitizeUrl = (url) => {
        if (!url) return "";
        const cleanedUrl = url.trim();
        if (cleanedUrl.includes("youtube.com") || cleanedUrl.includes("youtu.be")) {
          try {
            const urlObj = new URL(cleanedUrl);
            let videoId = "";
            if (cleanedUrl.includes("youtu.be/")) {
              videoId = urlObj.pathname.split("/").pop();
            } else if (urlObj.searchParams.has("v")) {
              videoId = urlObj.searchParams.get("v");
            }
            return videoId ? `https://www.youtube.com/watch?v=${videoId}` : cleanedUrl;
          } catch (e) {
            return cleanedUrl;
          }
        }
        return cleanedUrl;
      };

      const data = allUsers.map((user, index) => {
        const row = [
          index + 1,
          user.user_name,
          user.district_name || "N/A",
          selectedDate.format("MM/YYYY"),
        ];

        weeks.forEach((w) => {
          const report = user.weeks?.[w.value];
          // Reporting Period is chosen month year and week

          if (report) {
            row.push("Submitted");
            // Submitted on is the created at (formatted)
            row.push(dayjs(report.created_at).format("DD/MM/YYYY "));
            row.push(sanitizeUrl(report.report_pdf));
            row.push(
              report.supporting_media
                ? report.supporting_media.map((m) => sanitizeUrl(m)).join("\n")
                : ""
            );
          } else {
            row.push("Not Submitted");
            row.push("-");
            row.push("-");
            row.push("");
          }
        });
        return row;
      });

      const ws = XLSX.utils.aoa_to_sheet([header, ...data]);

      const wscols = [{ wch: 6 }, { wch: 25 }, { wch: 20 }];
      weeks.forEach(() => {
        wscols.push({ wch: 25 }); // Reporting Period
        wscols.push({ wch: 15 }); // Status
        wscols.push({ wch: 22 }); // Submitted On
        wscols.push({ wch: 30 }); // Report
        wscols.push({ wch: 60 }); // Media Links
      });
      ws["!cols"] = wscols;

      XLSX.utils.book_append_sheet(wb, ws, "Weekly Summary");
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `Weekly_Report_Summary_${selectedDate.format("MMM_YYYY")}.xlsx`);

      setNotification({ open: true, message: "Exported successfully", severity: "success" });
    } catch (error) {
      console.error("Export failed", error);
      setNotification({ open: true, message: "Export failed", severity: "error" });
    }
  };

  const weeks = getWeeksForMonth(selectedDate);
  const tableColSpan = 3 + weeks.length * 4;

  return (
    <DashboardLayout>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={3}
              py={3}
              px={3}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h6" color="white" fontWeight="medium">
                Weekly Reports Summary - {selectedDate.format("MMMM YYYY")}
              </MDTypography>

              <MDBox
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
                gap={1.5}
                sx={{ width: "100%" }}
              >
                <FormControl
                  size="small"
                  sx={{
                    flex: { xs: "1 1 100%", md: "1" },
                    minWidth: { md: "160px" },
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      "& .MuiInputBase-root": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      },
                    },
                  }}
                >
                  <InputLabel
                    id="district-select-label"
                    sx={{
                      color: "white !important",
                      fontSize: "14px",
                      fontWeight: 700,
                      transform: "translateY(-18px) translateX(3px)",

                      "&.Mui-focused": {
                        color: "white !important",
                      },
                    }}
                  >
                    District
                  </InputLabel>
                  <Select
                    labelId="district-select-label"
                    id="district-select"
                    multiple
                    value={selectedDistricts}
                    onChange={(e) => handleChange(e, setSelectedDistricts, selectedDistricts)}
                    input={<OutlinedInput label="District" />}
                    label="District"
                    renderValue={(selected) =>
                      selected.includes("All")
                        ? "All Districts"
                        : selected
                            .map((id) => districts.find((d) => d.value === id)?.title || id)
                            .join(", ")
                    }
                    sx={{
                      backgroundColor: "rgba(239, 254, 223, 0.9)",
                      borderRadius: "8px",
                      height: "38px",
                      fontSize: "13px",
                      fontWeight: 500,
                      transition: "all 0.3s ease",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                      },
                    }}
                  >
                    <MenuItem value="All">
                      <Checkbox checked={selectedDistricts.includes("All")} />
                      <ListItemText primary="All Districts" />
                    </MenuItem>
                    {districts?.map((district) => (
                      <MenuItem key={district.value} value={district.value}>
                        <Checkbox checked={selectedDistricts.indexOf(district.value) > -1} />
                        <ListItemText primary={district.title} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    flex: { xs: "1 1 100%", md: "1" },
                    minWidth: { md: "160px" },
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      "& .MuiInputBase-root": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      },
                    },
                  }}
                >
                  <InputLabel
                    id="user-select-label"
                    sx={{
                      color: "white !important",
                      fontSize: "14px",
                      fontWeight: 700,
                      transform: "translateY(-18px) translateX(3px)",
                      "&.Mui-focused": {
                        color: "white !important",
                      },
                    }}
                  >
                    User
                  </InputLabel>
                  <Select
                    labelId="user-select-label"
                    id="user-select"
                    multiple
                    value={selectedUsers}
                    onChange={(e) => handleChange(e, setSelectedUsers, selectedUsers)}
                    input={<OutlinedInput label="User" />}
                    label="User"
                    renderValue={(selected) => {
                      const uniqueSelected = [...new Set(selected.map(String))];
                      return uniqueSelected.includes("All")
                        ? "All Users"
                        : uniqueSelected
                            .map(
                              (id) =>
                                usersList.find(
                                  (u) => String(u.value) === id
                                )?.title || id
                            )
                            .join(", ");
                    }}
                    sx={{
                      backgroundColor: "rgba(239, 254, 223, 0.9)",
                      borderRadius: "8px",
                      height: "38px",
                      fontSize: "13px",
                      fontWeight: 500,
                      transition: "all 0.3s ease",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                      },
                    }}
                  >
                    <MenuItem value="All">
                      <Checkbox checked={selectedUsers.includes("All")} />
                      <ListItemText primary="All Users" />
                    </MenuItem>
                    {usersList.map((user) => (
                      <MenuItem key={user.value} value={user.value}>
                        <Checkbox
                          checked={
                            selectedUsers.indexOf(user.value) > -1 ||
                            selectedUsers.indexOf(String(user.value)) > -1 ||
                            (typeof user.value === "number" &&
                              selectedUsers.indexOf(parseInt(user.value, 10)) > -1)
                          }
                        />
                        <ListItemText primary={user.title} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    flex: { xs: "1 1 100%", md: "1" },
                    minWidth: { md: "160px" },
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      "& .MuiInputBase-root": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      },
                    },
                  }}
                >
                  <InputLabel
                    id="status-select-label"
                    sx={{
                      color: "white !important",
                      fontSize: "14px",
                      fontWeight: 700,
                      transform: "translateY(-18px) translateX(3px)",
                      "&.Mui-focused": {
                        color: "white !important",
                      },
                    }}
                  >
                    Status
                  </InputLabel>
                  <Select
                    labelId="status-select-label"
                    id="status-select"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(0);
                    }}
                    input={<OutlinedInput label="Status" />}
                    label="Status"
                    sx={{
                      backgroundColor: "rgba(239, 254, 223, 0.9)",
                      borderRadius: "8px",
                      height: "38px",
                      fontSize: "13px",
                      fontWeight: 500,
                      transition: "all 0.3s ease",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                      },
                    }}
                  >
                    <MenuItem value="All">All Status</MenuItem>
                    <MenuItem value="Submitted">Completed </MenuItem>
                    <MenuItem value="Pending">Pending </MenuItem>
                    <MenuItem value="Not Submitted">Not Submitted </MenuItem>
                  </Select>
                </FormControl>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box
                    sx={{
                      flex: { xs: "1 1 100%", md: "0.8" },
                      minWidth: { md: "150px" },
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        "& .MuiInputBase-root": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        },
                      },
                    }}
                  >
                    <DatePicker
                      views={["year", "month"]}
                      value={selectedDate}
                      onChange={(newValue) => setSelectedDate(newValue)}
                      // minDate={minDateLimit}
                      // maxDate={maxDateLimit}
                      slotProps={{
                        textField: {
                          size: "small",
                          InputProps: {
                            sx: {
                              height: "38px",
                              fontSize: "13px",
                              fontWeight: 500,
                            },
                          },
                          InputLabelProps: {
                            sx: {
                              fontSize: "14px",
                              fontWeight: 700,
                              transform: "translateY(-4px)",
                              color: "white !important",
                              "&.Mui-focused": {
                                color: "white !important",
                              },
                            },
                          },
                          sx: {
                            minWidth: "150px",
                            backgroundColor: "rgba(239, 254, 223, 0.9)",
                            borderRadius: "8px",
                            transition: "all 0.3s ease",
                            "& .MuiPickersInputBase-root": {
                              height: "38px",
                            },
                            "& .MuiSvgIcon-root": {
                              fontSize: "18px",
                            },
                            "& fieldset": {
                              border: "none",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>

                <MDButton
                  variant="gradient"
                  color="success"
                  size="small"
                  startIcon={<FileDownload />}
                  onClick={handleExportExcel}
                  sx={{
                    height: "38px",
                    flex: { xs: "1 1 100%", md: "0 0 auto" },
                    whiteSpace: "nowrap",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  Export Excel
                </MDButton>
              </MDBox>
            </MDBox>

            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "none",
                border: "1px solid #eee",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: "2905px", tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "65.7px" }} />
                  <col style={{ width: "163px" }} />
                  <col style={{ width: "164px" }} />
                  {weeks.map((w) => (
                    <React.Fragment key={`colgroup-${w.value}`}>
                      <col style={{ width: "117.1px" }} />
                      <col style={{ width: "117.1px" }} />
                      <col style={{ width: "117.1px" }} />
                      <col style={{ width: "117.1px" }} />
                    </React.Fragment>
                  ))}
                </colgroup>

                <TableHead
                  sx={{
                    "& th": {
                      boxSizing: "border-box",
                    },
                  }}
                >
                  <TableRow sx={{ bgcolor: "grey.100" }}>
                    <TableCell
                      rowSpan={2}
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        width: 120,
                        borderRight: "2px solid #ddd",
                      }}
                    >
                      S.No
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        fontWeight: "bold",
                        minWidth: 209,
                        maxWidth: 209,
                        borderRight: "2px solid #ddd",
                        textAlign: "center",
                      }}
                    >
                      <Tooltip
                        title={
                          sortField === "user_name" && sortOrder === "asc"
                            ? "Sort Descending"
                            : "Sort Ascending"
                        }
                      >
                        <TableSortLabel
                          active={true}
                          direction={sortField === "user_name" ? sortOrder : "asc"}
                          onClick={() => handleSort("user_name")}
                          sx={{
                            color:
                              sortField === "user_name"
                                ? "inherit !important"
                                : "rgba(0, 0, 0, 0.8) !important",
                            "& .MuiTableSortLabel-icon": {
                              opacity:
                                sortField === "user_name" ? "1 !important" : "0.3 !important",
                              color:
                                sortField === "user_name"
                                  ? "inherit !important"
                                  : "rgba(0, 0, 0, 0.4) !important",
                            },
                            "&.Mui-active": {
                              color:
                                sortField === "user_name"
                                  ? "inherit !important"
                                  : "rgba(0, 0, 0, 0.8) !important",
                            },
                          }}
                        >
                          User Name
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        fontWeight: "bold",
                        minWidth: 210,
                        maxWidth: 210,
                        borderRight: "2px solid #ddd",
                        textAlign: "center",
                      }}
                    >
                      <Tooltip
                        title={
                          sortField === "district_name" && sortOrder === "asc"
                            ? "Sort Descending"
                            : "Sort Ascending"
                        }
                      >
                        <TableSortLabel
                          active={true}
                          direction={sortField === "district_name" ? sortOrder : "asc"}
                          onClick={() => handleSort("district_name")}
                          sx={{
                            color:
                              sortField === "district_name"
                                ? "inherit !important"
                                : "rgba(0, 0, 0, 0.8) !important",
                            "& .MuiTableSortLabel-icon": {
                              opacity:
                                sortField === "district_name" ? "1 !important" : "0.3 !important",
                              color:
                                sortField === "district_name"
                                  ? "inherit !important"
                                  : "rgba(0, 0, 0, 0.4) !important",
                            },
                            "&.Mui-active": {
                              color:
                                sortField === "district_name"
                                  ? "inherit !important"
                                  : "rgba(0, 0, 0, 0.8) !important",
                            },
                          }}
                        >
                          District
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                    {weeks.map((w) => (
                      <TableCell
                        key={w.value}
                        colSpan={4}
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          borderRight: "2px solid #ddd",
                          bgcolor: `rgba(26, 115, 232, ${0.05 * w.value})`,
                          minWidth: 600,
                        }}
                      >
                        {w.label}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    {weeks.map((w) => (
                      <React.Fragment key={`sub-${w.value}`}>
                        <TableCell
                          align="center"
                          sx={{
                            fontSize: "12px",
                            fontWeight: "600",
                            minWidth: 147,
                            maxWidth: 147,
                            borderRight: "1px solid #ddd",
                          }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontSize: "12px",
                            minWidth: 147,
                            maxWidth: 147,
                            fontWeight: "600",
                            borderRight: "1px solid #ddd",
                          }}
                        >
                          Submitted On
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontSize: "12px",
                            minWidth: 147,
                            maxWidth: 147,
                            fontWeight: "600",
                            borderRight: "1px solid #ddd",
                          }}
                        >
                          Report
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontSize: "12px",
                            minWidth: 147,
                            maxWidth: 147,
                            fontWeight: "600",
                            borderRight: "2px solid #ddd",
                          }}
                        >
                          Supporting Media
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={tableColSpan} align="center" sx={{ py: 5 }}>
                        <CircularProgress color="info" size={30} />
                        <MDTypography variant="button" display="block" mt={1}>
                          Loading Summary...
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableColSpan} align="center" sx={{ py: 3 }}>
                        No users found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user, index) => (
                      <TableRow key={user.user_id} sx={{ "&:hover": { bgcolor: "grey.50" } }}>
                        <TableCell
                          align="center"
                          sx={{ borderRight: "2px solid #eee", fontWeight: "500" }}
                        >
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell sx={{ borderRight: "2px solid #eee", fontWeight: "500" }}>
                          {user.user_name}
                        </TableCell>
                        <TableCell sx={{ borderRight: "2px solid #eee" }}>
                          {user.district_name || "N/A"}
                        </TableCell>
                        {weeks.map((w) => {
                          const report = user.weeks?.[w.value];
                          return (
                            <React.Fragment key={`cell-${user.user_id}-${w.value}`}>
                              <TableCell align="center" sx={{ borderRight: "1px solid #eee" }}>
                                {report ? (
                                  <Tooltip title="Submitted">
                                    Submitted{" "}
                                    <CheckCircle sx={{ color: "success.main", fontSize: 18 }} />
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Not Submitted">
                                    Not Submitted{" "}
                                    <Cancel
                                      sx={{ color: "error.light", fontSize: 18, opacity: 0.5 }}
                                    />
                                  </Tooltip>
                                )}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontSize: "12px", borderRight: "1px solid #eee" }}
                              >
                                {report ? dayjs(report.created_at).format("DD/MM/YY") : "-"}
                              </TableCell>
                              <TableCell align="center" sx={{ borderRight: "1px solid #eee" }}>
                                {report ? (
                                  <MDBox display="flex" flexDirection="column" gap={1} alignItems="center">
                                    {(() => {
                                      let urls = [];
                                      try {
                                        const parsed = JSON.parse(report.report_pdf);
                                        urls = Array.isArray(parsed) ? parsed : [report.report_pdf];
                                      } catch (e) {
                                        urls = [report.report_pdf];
                                      }

                                      return urls.map((url, idx) => (
                                        <DocumentActions
                                          key={idx}
                                          url={url}
                                          title={`${user.user_name}_Week_${w.value}_${idx + 1}`}
                                          isPdf={url.toLowerCase().split("?")[0].endsWith(".pdf")}
                                          isWord={
                                            url.toLowerCase().split("?")[0].endsWith(".doc") ||
                                            url.toLowerCase().split("?")[0].endsWith(".docx")
                                          }
                                          onDelete={() =>
                                            setDeleteFileConfirmation({
                                              open: true,
                                              reportId: report.report_id,
                                              fileUrl: url,
                                              userName: user.user_name,
                                              weekNumber: w.value,
                                            })
                                          }
                                        />
                                      ));
                                    })()}
                                    {/* <Tooltip title="Delete Report">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          setDeleteConfirmation({
                                            open: true,
                                            reportId: report.report_id,
                                            userName: user.user_name,
                                            weekNumber: w.value,
                                          })
                                        }
                                        sx={{
                                          bgcolor: "#ffebee",
                                          "&:hover": { bgcolor: "#ffcdd2" },
                                          width: 24,
                                          height: 24,
                                          mt: 0.5,
                                        }}
                                      >
                                        <Icon sx={{ fontSize: "1rem" }}>delete</Icon>
                                      </IconButton>
                                    </Tooltip> */}
                                  </MDBox>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell align="center" sx={{ borderRight: "2px solid #eee" }}>
                                {report && report.supporting_media?.length > 0 ? (
                                  <Box display="flex" justifyContent="center">
                                    <Tooltip title={`${report.supporting_media.length} files`}>
                                      <Box display="flex" gap={0.5}>
                                        {report.supporting_media.slice(0, 2).map((url, idx) => {
                                          const lowerUrl = url.toLowerCase();
                                          return (
                                            <IconButton
                                              key={idx}
                                              size="small"
                                              sx={{ p: 0.2 }}
                                              onClick={() => window.open(url, "_blank")}
                                            >
                                              {lowerUrl.includes("youtube.com") ||
                                              lowerUrl.includes("youtu.be") ? (
                                                <YouTube
                                                  sx={{ fontSize: 16, color: "error.main" }}
                                                />
                                              ) : lowerUrl.includes("drive.google.com") ? (
                                                <InsertDriveFile
                                                  sx={{ fontSize: 16, color: "info.main" }}
                                                />
                                              ) : lowerUrl.match(/\.(mp4|mov|quicktime)$/) ? (
                                                <PlayCircle
                                                  sx={{ fontSize: 16, color: "info.main" }}
                                                />
                                              ) : lowerUrl.endsWith(".pdf") ? (
                                                <PictureAsPdf
                                                  sx={{ fontSize: 16, color: "error.main" }}
                                                />
                                              ) : lowerUrl.match(/\.(jpg|jpeg|png|gif|webp)$/) ? (
                                                <ImageIcon
                                                  sx={{ fontSize: 16, color: "success.main" }}
                                                />
                                              ) : (
                                                <Link sx={{ fontSize: 16, color: "info.main" }} />
                                              )}
                                            </IconButton>
                                          );
                                        })}
                                        {report.supporting_media.length > 2 && (
                                          <MDTypography
                                            variant="caption"
                                            sx={{
                                              fontSize: "10px",
                                              alignSelf: "center",
                                              cursor: "pointer",
                                              color: "#1A73E8",
                                              fontWeight: "bold",
                                              ml: 0.5,
                                              "&:hover": { textDecoration: "underline" },
                                            }}
                                            onClick={() =>
                                              handleOpenMediaDialog(
                                                report.supporting_media,
                                                user.user_name
                                              )
                                            }
                                          >
                                            +{report.supporting_media.length - 2} more
                                          </MDTypography>
                                        )}
                                      </Box>
                                    </Tooltip>
                                  </Box>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                            </React.Fragment>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Card>
        </Grid>
      </Grid>

      <Dialog open={Boolean(viewPdfUrl)} onClose={handleClosePdf} maxWidth="lg" fullWidth>
        <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Document Preview</Typography>
          <IconButton onClick={handleClosePdf}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "80vh" }}>
          {viewPdfUrl && (
            <iframe
              src={viewPdfUrl}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title="Document Preview"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Supporting Media List Dialog */}
      <Dialog open={mediaListDialog.open} onClose={handleCloseMediaDialog} maxWidth="sm" fullWidth>
        <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h6">Supporting Media - {mediaListDialog.userName}</MDTypography>
          <IconButton onClick={handleCloseMediaDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2}>
            {mediaListDialog.media.map((url, idx) => {
              const lowerUrl = url.toLowerCase();
              const fileName = getFileName(url);
              return (
                <MDBox
                  key={idx}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p={1.5}
                  sx={{ bgcolor: "#f8f9fa", borderRadius: 2, border: "1px solid #eee" }}
                >
                  <MDBox display="flex" alignItems="center" gap={1.5} sx={{ overflow: "hidden" }}>
                    {lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be") ? (
                      <YouTube sx={{ color: "#ff0000" }} />
                    ) : lowerUrl.includes("drive.google.com") ? (
                      <InsertDriveFile sx={{ color: "#1A73E8" }} />
                    ) : lowerUrl.match(/\.(mp4|mov|quicktime)$/) ? (
                      <PlayCircle sx={{ color: "#1A73E8" }} />
                    ) : lowerUrl.endsWith(".pdf") ? (
                      <PictureAsPdf sx={{ color: "#f44336" }} />
                    ) : lowerUrl.match(/\.(jpg|jpeg|png|gif|webp)$/) ? (
                      <ImageIcon sx={{ color: "#4CAF50" }} />
                    ) : (
                      <Link sx={{ color: "#757575" }} />
                    )}
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: { xs: "150px", sm: "300px" },
                      }}
                    >
                      {fileName}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" gap={1}>
                    <Tooltip title="View/Open">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => window.open(url, "_blank")}
                        sx={{ bgcolor: "#e3f2fd" }}
                      >
                        <Visibility sx={{ fontSize: "1.1rem" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleDownload(url, fileName)}
                        sx={{ bgcolor: "#e8f5e9" }}
                      >
                        <Download sx={{ fontSize: "1.1rem" }} />
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                </MDBox>
              );
            })}
          </MDBox>
        </DialogContent>
        <Box p={2} display="flex" justifyContent="flex-end">
          <MDButton onClick={handleCloseMediaDialog} color="info" variant="gradient">
            Close
          </MDButton>
        </Box>
      </Dialog>

      <Dialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ ...deleteConfirmation, open: false })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete the weekly report for <b>{deleteConfirmation.userName}</b> (Week {deleteConfirmation.weekNumber})?
            This will permanently remove the files from storage and the database.
          </Typography>
        </DialogContent>
        <MDBox display="flex" justifyContent="flex-end" gap={2} p={2}>
          <MDButton
            variant="text"
            color="secondary"
            onClick={() => setDeleteConfirmation({ ...deleteConfirmation, open: false })}
          >
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={handleDeleteReport} autoFocus>
            Confirm Delete
          </MDButton>
        </MDBox>
      </Dialog>

      {/* Individual File Delete Confirmation Dialog */}
      <Dialog
        open={deleteFileConfirmation.open}
        onClose={() =>
          setDeleteFileConfirmation({
            open: false,
            reportId: null,
            fileUrl: null,
            userName: "",
            weekNumber: "",
          })
        }
        aria-labelledby="delete-file-dialog-title"
      >
        <DialogTitle id="delete-file-dialog-title">Delete File Confirmation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text">
            Are you sure you want to delete this specific file from <b>{deleteFileConfirmation.userName}</b>&apos;s
            report for <b>Week {deleteFileConfirmation.weekNumber}</b>?
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 2, display: "block" }}>
            This action will permanently remove the file from storage and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="text"
            color="secondary"
            onClick={() =>
              setDeleteFileConfirmation({
                open: false,
                reportId: null,
                fileUrl: null,
                userName: "",
                weekNumber: "",
              })
            }
          >
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={handleDeleteFile} autoFocus>
            Delete File
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open || Boolean(message.status)}
        autoHideDuration={3000}
        onClose={() => {
          setNotification({ ...notification, open: false });
          setMessage({ status: "", title: "", msg: "" });
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => {
            setNotification({ ...notification, open: false });
            setMessage({ status: "", title: "", msg: "" });
          }}
          severity={message.status || notification.severity || "info"}
          variant="filled"
          sx={{ width: "100%", color: "white" }}
        >
          {message.msg || notification.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};;

export default AdminWeeklyReportManager;
